// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Pause from "@bg/XDL/Icons/Pause";
import Play from "@bg/XDL/Icons/Play";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useEmblaCarousel } from "embla-carousel/react";
import parse from "html-react-parser";
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useState } from "react";
import { css } from "styled-components";

interface SlideModel {
    fields: {
        slideTitle: string;
        background: "image" | "color";
        videoUrl: string;
        image: string;
        mediumImage: string;
        smallImage: string;
        imageOverlay: string;
        partialOverlay: boolean;
        partialOverlayPositioning: "top" | "middle" | "bottom";
        responsiveImageBehavior: "cover" | "center" | "prioritizeHeight" | "prioritizeWidth";
        backgroundColor: string;
        heading: string;
        subheading: string;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
        buttonAlignment: "start" | "center" | "end";
        buttonVariant: "primary" | "secondary" | "tertiary";
        focalPoint:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        contentPadding: number;
        centerTextVertically: boolean;
    };
}

const enum fields {
    height = "height",
    specificHeight = "specificHeight",
    textAlignment = "textAlignment",
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    slides = "slides",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.height]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.specificHeight]: number;
        [fields.textAlignment]: "left" | "right";
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.slides]: SlideModel[];
    };
    extendedStyles?: SlideshowStyles;
}

export interface SlideshowStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    textCarouselWrapper?: InjectableCss;
    textCarouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    textSlideContainerWrapper?: InjectableCss;
    textSlideContentWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotsInnerContainer?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    separatorStick?: InjectableCss;
}

export const slideshowStyles: SlideshowStyles = {
    slideshowWrapper: {
        css: css`
            padding: 0;
            position: relative;
            display: flex;
            flex-wrap: wrap;
            @media (max-width: 950px) {
                flex-direction: column;
            }
        `,
    },
    prevArrowWrapper: {
        css: css`
            margin-right: 10px;
            padding-top: 3px;
            opacity: 0.7;
        `,
    },
    prevArrowButton: {
        css: css`
            background: transparent !important;
            border: 0;
            padding: 0;
            min-height: auto;
            &:hover {
                background: none;
            }
            &:focus,
            &:active {
                outline: none !important;
                box-shadow: none;
            }
        `,
    },
    iconProps: {
        size: 20,
        css: css`
            svg {
                color: ${getColor("tertiary.main")};
            }
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    textCarouselWrapper: {
        css: css`
            @media (max-width: 950px) {
                position: absolute;
                bottom: 0;
                width: 100%;
                z-index: 2;
            }
        `,
    },
    textCarouselContainer: {
        css: css`
            overflow: hidden;
            background: ${getColor("common.ui4")};
            @media (max-width: 950px) {
                min-height: auto;
                max-height: 100%;
                overflow-y: auto;
                background: rgba(243, 243, 243, 0.9);
            }
        `,
    },
    textSlideContainerWrapper: {
        css: css`
            display: flex;
            @media (max-width: 950px) {
                min-height: auto;
                max-height: 100%;
                width: 100%;
            }
        `,
    },
    textSlideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            align-items: center;
            position: relative;
            @media (max-width: 950px) {
                padding: 20px 20px 40px;
            }
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            color: white;
            background-repeat: no-repeat;
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
    slideButton: {
        css: css`
            &&&& {
                width: fit-content;
                min-height: 36px;
                font-size: ${fontConfig.fontSize.small}px;
                letter-spacing: 1px;
                @media (max-width: 500px) {
                    width: 100%;
                }
            }
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            bottom: 45px;
            position: absolute;
            z-index: 3;
            @media (max-width: 950px) {
                bottom: 5px;
                width: 100%;
                justify-content: center;
                right: auto;
            }
        `,
    },
    dotsInnerContainer: {
        css: css`
            display: flex;
            align-items: center;
            max-width: 500px;
            width: 100%;
            @media (max-width: 1024px) {
                padding-left: 20px;
            }
            @media (max-width: 950px) {
                max-width: 100%;
                justify-content: center;
                padding: 0;
            }
        `,
    },
    dotButton: {
        css: css`
            height: 8px;
            min-height: auto;
            width: 8px;
            padding: 0;
            margin: 0 4px;
            border: 1px solid ${getColor("tertiary")} !important;
            border-radius: 50% !important;
            background: none !important;
            &:hover,
            &:active {
                background: ${getColor("tertiary.main")} !important;
                box-shadow: none;
                outline: none !important;
            }
            &:focus {
                outline: none !important;
                box-shadow: none;
            }
        `,
    },
    nextArrowWrapper: {
        css: css`
            margin-left: 10px;
            padding-top: 3px;
            opacity: 0.7;
        `,
    },
    nextArrowButton: {
        css: css`
            background: transparent !important;
            border: 0;
            padding: 0;
            min-height: auto;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none !important;
                box-shadow: none;
            }
        `,
    },
    slideCenteringWrapperStyles: {
        css: css`
            padding: 0 70px 0 30px;
            max-width: 500px;

            @media (max-width: 1024px) {
                max-width: none;
                padding: 0 20px;
            }

            @media (max-width: 950px) {
                padding: 0;
            }
        `,
    },
    headingText: {
        variant: "h1",
        css: css`
            & > ${TypographyStyle} {
                font-size: inherit;
                font-weight: inherit;
                line-height: inherit;
                margin-bottom: 0;
            }
        `,
    },
    subheadingText: {
        variant: "p",
        lineHeight: "24px",
        css: css`
            margin-bottom: 30px;
            & > ${TypographyStyle} {
                font-size: inherit;
                font-weight: inherit;
                line-height: inherit;
                margin-bottom: 0;
            }
        `,
    },
    separatorStick: {
        css: css`
            height: 11px;
            width: 11px;
            position: relative;
            margin-right: 24px;
            margin-left: 10px;

            &:after {
                content: " ";
                position: absolute;
                height: 20px;
                width: 1px;
                background: ${getColor("tertiary.main")};
                right: -15px;
                top: -5px;
                opacity: 0.5;
            }
            &:hover {
                cursor: pointer;
            }
        `,
    },
};

const Slideshow = ({ fields, extendedStyles, id }: OwnProps) => {
    const { isInShell } = useShellContext();
    const history = useHistory();
    const buttonLinks = useGetLinks(fields.slides, o => o.fields.buttonLink);
    const [styles] = useState(() => mergeToNew(slideshowStyles, extendedStyles));

    const [emblaRef, embla] = useEmblaCarousel();
    const [emblaSecondRef, emblaSecond] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);
    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    useEffect(() => {
        if (!emblaSecond) {
            return;
        }

        emblaSecond.on("select", () => {
            setSelectedIndex(emblaSecond.selectedScrollSnap());
        });
    }, [emblaSecond]);

    const [pause, setPause] = React.useState(false);
    const scrollNext = useCallback(() => {
        if (pause) {
            return;
        }
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla, pause]);

    const scrollNextSecondEmbla = useCallback(() => {
        if (pause) {
            return;
        }
        if (!emblaSecond) {
            return;
        }

        emblaSecond.scrollNext();
    }, [emblaSecond, pause]);

    useRecursiveTimeout(scrollNext, fields.autoplay * 1000);
    useRecursiveTimeout(scrollNextSecondEmbla, fields.autoplay * 1000);

    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
            draggable: false,
        });
    }, [embla, fields.slides]);

    useEffect(() => {
        if (!emblaSecond || !fields.slides || fields.slides.length === 0) {
            return;
        }

        emblaSecond.reInit({
            align: "start",
            loop: true,
            draggable: false,
        });
    }, [emblaSecond, fields.slides]);

    const onClickButton = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    if (!fields.slides || fields.slides.length === 0) {
        return null;
    }

    let heightStyles: string;
    if (!fields.specificHeight) {
        switch (fields.height) {
            case "1/4 viewport":
                heightStyles = "min-height: 25vh;";
                break;
            case "1/2 viewport":
                heightStyles = "min-height: 50vh;";
                break;
            case "3/4 viewport":
                heightStyles = "min-height: 75vh;";
                break;
            case "fullViewport":
                heightStyles = "min-height: 100vh;";
                break;
        }
    } else {
        heightStyles = `min-height: ${fields.specificHeight}px; max-height: ${fields.specificHeight}px;`;
    }

    const [secondWidth, setSecondWidth] = useState(-10000);

    React.useEffect(() => {
        const updateSize = debounce(() => {
            const width = (window.innerWidth - 1360) / 2 + 500;
            setSecondWidth(width);
        }, 150);
        window.addEventListener("resize", updateSize);
        updateSize();
        return () => window.removeEventListener("resize", updateSize);
    }, []);

    return (
        <>
            <StyledWrapper {...styles.slideshowWrapper}>
                <StyledWrapper
                    css={css`
                        ${styles.textCarouselWrapper?.css}
                        width: ${secondWidth}px;
                        position: relative;
                        order: ${fields.textAlignment === "left" ? 1 : 2};
                    `}
                >
                    <StyledWrapper
                        css={css`
                            ${styles.textCarouselContainer?.css}
                            ${heightStyles}
                        `}
                        ref={emblaSecondRef}
                    >
                        <StyledWrapper
                            css={css`
                                ${styles.textSlideContainerWrapper?.css}
                                ${heightStyles}
                            `}
                        >
                            {fields.slides.map((slide, index) => {
                                const buttonLink = buttonLinks[index];
                                const backgroundStyles = `background-color: ${
                                    slide.fields.backgroundColor ? slide.fields.backgroundColor : `transparent`
                                };`;

                                const buttonWrapperStyles = {
                                    css: css`
                                        display: flex;
                                        width: 100%;
                                        justify-content: ${slide.fields.buttonAlignment === "center"
                                            ? "center"
                                            : `flex-${slide.fields.buttonAlignment}`};
                                    `,
                                };

                                return (
                                    <StyledWrapper
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`second_carousel_${index}`}
                                        css={css`
                                            ${styles.textSlideContentWrapper?.css}
                                            justify-content: ${fields.textAlignment === "left"
                                                ? "flex-end"
                                                : "flex-start"};
                                        `}
                                    >
                                        {(slide.fields.heading ||
                                            slide.fields.subheading ||
                                            slide.fields.buttonLabel ||
                                            slide.fields.buttonLink.value) && (
                                            <StyledWrapper
                                                css={css`
                                                    ${styles.slideCenteringWrapperStyles
                                                        ?.css} padding: ${fields.textAlignment === "left"
                                                        ? "0 70px 0 30px"
                                                        : "0 30px 0 70px"};
                                                `}
                                            >
                                                {slide.fields.heading && (
                                                    <Typography {...styles.headingText}>
                                                        {parse(slide.fields.heading, parserOptions)}
                                                    </Typography>
                                                )}
                                                {slide.fields.subheading && (
                                                    <Typography {...styles.subheadingText}>
                                                        {parse(slide.fields.subheading, parserOptions)}
                                                    </Typography>
                                                )}
                                                {(slide.fields.buttonLabel || slide.fields.buttonLink.value) && (
                                                    <StyledWrapper {...buttonWrapperStyles}>
                                                        <Button
                                                            {...styles.slideButton}
                                                            variant={slide.fields.buttonVariant}
                                                            onClick={() => onClickButton(buttonLink?.url)}
                                                        >
                                                            {slide.fields.buttonLabel ||
                                                                buttonLink?.title ||
                                                                buttonLink?.url}
                                                        </Button>
                                                    </StyledWrapper>
                                                )}
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                );
                            })}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper
                    css={css`
                        position: relative;
                        flex-grow: 1;
                        order: ${fields.textAlignment === "left" ? 2 : 1};
                    `}
                >
                    <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                        <StyledWrapper {...styles.slideContainerWrapper}>
                            {fields.slides.map((slide, index) => {
                                let responsiveImageBehaviorStyles;
                                if (slide.fields.responsiveImageBehavior === "cover") {
                                    responsiveImageBehaviorStyles = "background-size: cover;";
                                } else if (slide.fields.responsiveImageBehavior === "center") {
                                    responsiveImageBehaviorStyles = "background-size: contain;";
                                } else if (slide.fields.responsiveImageBehavior === "prioritizeHeight") {
                                    responsiveImageBehaviorStyles = "background-size: auto 100%;";
                                } else if (slide.fields.responsiveImageBehavior === "prioritizeWidth") {
                                    responsiveImageBehaviorStyles = "background-size: 100% auto;";
                                }

                                let backgroundStyles;

                                if (slide.fields.background === "image") {
                                    backgroundStyles = `
                                background-image: url(${parseImageUrl(slide.fields.image).url});
                                @media (max-width: 1024px) {
                                    background-image: url(${
                                        parseImageUrl(
                                            slide.fields.mediumImage ? slide.fields.mediumImage : slide.fields.image,
                                        ).url
                                    });
                                }
                                @media (max-width: 768px) {
                                    background-image: url(${
                                        parseImageUrl(
                                            slide.fields.smallImage ? slide.fields.smallImage : slide.fields.image,
                                        ).url
                                    });
                                }
                            `;
                                } else {
                                    backgroundStyles = `background-color: ${slide.fields.backgroundColor};`;
                                }

                                const focalPointStyles = getFocalPointStyles(slide.fields.focalPoint);

                                const slideWrapperStyles = {
                                    css: css`
                                        ${styles.slideContentWrapper?.css || ""}
                                        ${heightStyles}
                                ${backgroundStyles}
                                ${responsiveImageBehaviorStyles}
                                ${focalPointStyles}
                                    `,
                                };

                                let video: any;
                                if (slide.fields.videoUrl?.length) {
                                    video = (
                                        <video
                                            autoPlay
                                            muted
                                            loop
                                            playsInline
                                            height="440px"
                                            style={{
                                                position: "absolute",
                                                left: 0,
                                                top: 0,
                                                right: 0,
                                                bottom: 0,
                                                objectFit: "cover",
                                                width: "100%",
                                                height: "100%",
                                            }}
                                        >
                                            <source src={slide.fields.videoUrl} type="video/mp4" />
                                            <source src={slide.fields.videoUrl} type="video/webm" />
                                            <source src={slide.fields.videoUrl} type="video/ogg" />
                                            {translate("Your Browser Does Not Support HTML 5 Video ")}
                                        </video>
                                    );
                                }
                                return (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <StyledWrapper key={index} {...slideWrapperStyles}>
                                        {video}
                                    </StyledWrapper>
                                );
                            })}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper
                    css={css`
                        ${styles.dotsContainerWrapper?.css} width: ${secondWidth}px;
                        justify-content: ${fields.textAlignment === "left" ? "flex-end" : "flex-start"};
                        ${fields.textAlignment === "right" ? "right: 0;" : ""}
                    `}
                >
                    <StyledWrapper
                        css={css`
                            ${styles.dotsInnerContainer?.css} padding-left: ${fields.textAlignment === "left"
                                ? "30px"
                                : "70px"};
                        `}
                    >
                        {fields.showArrows && fields.slides.length > 1 && (
                            <StyledWrapper {...styles.prevArrowWrapper}>
                                <Button
                                    {...styles.prevArrowButton}
                                    onClick={() => {
                                        embla && embla.scrollPrev();
                                        emblaSecond && emblaSecond.canScrollPrev() && emblaSecond.scrollPrev();
                                    }}
                                    aria-label={translate("Previous")}
                                >
                                    <ButtonIcon {...styles.iconProps} src="ChevronLeft" />
                                </Button>
                            </StyledWrapper>
                        )}
                        {fields.slides.length > 1 && fields.showArrows && (
                            <Typography weight={fontConfig.weight.semiBold} italic>{`${selectedIndex + 1} of ${
                                fields.slides.length
                            }`}</Typography>
                        )}
                        {fields.showArrows && fields.slides.length > 1 && (
                            <StyledWrapper {...styles.nextArrowWrapper}>
                                <Button
                                    {...styles.nextArrowButton}
                                    disabled={
                                        embla && !embla.canScrollNext() && emblaSecond && !emblaSecond.canScrollNext()
                                    }
                                    onClick={() => {
                                        embla && embla.scrollNext();
                                        emblaSecond && emblaSecond.canScrollNext() && emblaSecond.scrollNext();
                                    }}
                                    aria-label={translate("Next")}
                                >
                                    <ButtonIcon {...styles.iconProps} src="ChevronRight" />
                                </Button>
                            </StyledWrapper>
                        )}
                        {fields.slideIndicator && (
                            <>
                                <StyledWrapper {...styles.separatorStick} onClick={() => setPause(!pause)}>
                                    <Icon
                                        size={11}
                                        color="tertiary.main"
                                        src={pause ? Play : Pause}
                                        css={css`
                                            position: absolute;
                                        `}
                                    />
                                </StyledWrapper>

                                {fields.slides.map((_, i) => {
                                    const dotColorStyles =
                                        i === selectedIndex
                                            ? css`
                                                  background: #9c822f !important;
                                                  &:hover {
                                                      background: #9c822f !important;
                                                  }
                                              `
                                            : "";
                                    const dotButtonStyles = {
                                        css: css`
                                            ${styles.dotButton?.css || ""}
                                            ${dotColorStyles}
                                        `,
                                    };
                                    return (
                                        <Button
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={i}
                                            {...dotButtonStyles}
                                            onClick={() => {
                                                embla && embla.scrollTo(i);
                                                emblaSecond && emblaSecond.scrollTo(i);
                                            }}
                                            aria-label={translate("Scroll to {0}", `${i + 1}`)}
                                        />
                                    );
                                })}
                            </>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <Zone contentId={id} zoneName="globalMessage" />
        </>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Slideshow,
    definition: {
        group: "XNGAGE" as any,
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.height,
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.specificHeight,
                displayName: "Specific Height",
                fieldType: "General",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                tab: settingsTab,
            },
            {
                name: fields.textAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: (item: HasFields) => {
                    return item.fields.slideTitle;
                },
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "background",
                        displayName: "Background",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Image", value: "image" },
                            { displayName: "Color", value: "color" },
                        ],
                        defaultValue: "image",
                        hideEmptyOption: true,
                    },
                    {
                        name: "videoUrl",
                        displayName: "Video Url",
                        editorTemplate: "ImagePickerField",
                        isVisible: widget => widget.fields.background !== "image",
                        defaultValue: "",
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "mediumImage",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "smallImage",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "imageOverlay",
                        displayName: "Image Color Overlay",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "partialOverlay",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "partialOverlayPositioning",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top", value: "top" },
                            { displayName: "Middle", value: "middle" },
                            { displayName: "Bottom", value: "bottom" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "bottom",
                        isVisible: widget => widget.fields.partialOverlay,
                    },
                    {
                        name: "responsiveImageBehavior",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Cover", value: "cover" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Prioritize height", value: "prioritizeHeight" },
                            { displayName: "Prioritize width", value: "prioritizeWidth" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "cover",
                    },
                    {
                        name: "backgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Color",
                        defaultValue: "black",
                        isVisible: widget => widget.fields.background === "color",
                    },
                    {
                        name: "focalPoint",
                        displayName: "Focal Point",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top Left", value: "topLeft" },
                            { displayName: "Top Center", value: "topCenter" },
                            { displayName: "Top Right", value: "topRight" },
                            { displayName: "Center Left", value: "centerLeft" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Center Right", value: "centerRight" },
                            { displayName: "Bottom Left", value: "bottomLeft" },
                            { displayName: "Bottom Center", value: "bottomCenter" },
                            { displayName: "Bottom Right", value: "bottomRight" },
                        ],
                        defaultValue: "center",
                        hideEmptyOption: true,
                    },
                    {
                        name: "heading",
                        editorTemplate: "RichTextField",
                        displayName: "Heading",
                        defaultValue: "",
                        extendedConfig: { height: 100 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "subheading",
                        editorTemplate: "RichTextField",
                        displayName: "Subheading",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "buttonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "buttonAlignment",
                        displayName: "Button Alignment",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Left", value: "start" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Right", value: "end" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "start",
                    },
                    {
                        name: "buttonVariant",
                        displayName: "Button Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                    },
                    {
                        name: "contentPadding",
                        editorTemplate: "IntegerField",
                        defaultValue: 50,
                    },
                    {
                        name: "centerTextVertically",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
